import Phaser from 'phaser';

export interface ButtonStyle {
  fill?: number;
  hoverFill?: number;
  textColor?: string;
  fontSize?: string;
  padding?: number;
  radius?: number;
  stroke?: number;
  strokeWidth?: number;
}

export interface ButtonOptions extends ButtonStyle {
  width?: number;
  height?: number;
  onClick?: () => void;
}

export class Button extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;
  private hoverFill: number;
  private restFill: number;
  private _enabled = true;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    opts: ButtonOptions = {},
  ) {
    super(scene, x, y);
    const {
      fill = 0xf7901e,
      hoverFill = 0xffb24a,
      textColor = '#ffffff',
      fontSize = '24px',
      padding = 16,
      radius = 12,
      stroke = 0xffffff,
      strokeWidth = 2,
      width,
      height,
      onClick,
    } = opts;
    this.restFill = fill;
    this.hoverFill = hoverFill;

    this.label = scene.add.text(0, 0, text, {
      fontFamily: '"Comic Sans MS", "Trebuchet MS", sans-serif',
      fontSize,
      color: textColor,
    }).setOrigin(0.5);

    const w = width ?? this.label.width + padding * 2;
    const h = height ?? this.label.height + padding;
    this.bg = scene.add.rectangle(0, 0, w, h, fill).setStrokeStyle(strokeWidth, stroke);
    if (radius > 0) {
      // Phaser Rectangle doesn't support radius natively; layer a Graphics behind for rounded look.
      const g = scene.add.graphics();
      g.fillStyle(fill, 1);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
      g.lineStyle(strokeWidth, stroke, 1);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);
      this.bg.setVisible(false);
      this.add(g);
      this._gfx = g;
      this._size = { w, h, radius, stroke, strokeWidth };
    }

    this.add([this.bg, this.label]);
    this.setSize(w, h);
    this.setInteractive({ useHandCursor: true });

    this.on('pointerover', () => this.setFill(this.hoverFill));
    this.on('pointerout', () => this.setFill(this.restFill));
    this.on('pointerdown', () => this.setScale(0.97));
    this.on('pointerup', () => this.setScale(1));
    if (onClick) this.on('pointerup', () => { if (this._enabled) onClick(); });

    scene.add.existing(this);
  }

  private _gfx?: Phaser.GameObjects.Graphics;
  private _size?: { w: number; h: number; radius: number; stroke: number; strokeWidth: number };

  setText(text: string): this {
    this.label.setText(text);
    return this;
  }

  setFill(color: number): this {
    if (this._gfx && this._size) {
      const { w, h, radius, stroke, strokeWidth } = this._size;
      this._gfx.clear();
      this._gfx.fillStyle(color, 1);
      this._gfx.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
      this._gfx.lineStyle(strokeWidth, stroke, 1);
      this._gfx.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);
    } else {
      this.bg.setFillStyle(color);
    }
    return this;
  }

  setEnabled(enabled: boolean): this {
    this._enabled = enabled;
    this.setAlpha(enabled ? 1 : 0.5);
    if (enabled) this.setInteractive({ useHandCursor: true });
    else this.disableInteractive();
    return this;
  }
}
